import { type UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import {
  type ApplicationListQuery,
  type HrDashboardRange,
  deleteApplication,
  getHrDashboard,
  listApplications,
  updateApplicationStatus,
} from './careers.api';

import type {
  CareerApplicationDetail,
  CareerApplicationStatus,
  PaginatedResponse,
} from '@utazzo/shared';

const KEY = 'career-applications';

/**
 * Recruitment snapshot for the HR dashboard. `enabled` is false for roles that
 * would 403 on the endpoint, so a non-HR caller never fires the request.
 */
export const useHrDashboard = (range?: HrDashboardRange, enabled = true) =>
  useQuery({
    // Range is part of the key so the dashboard's windowed view and the
    // enquiries tab's all-time role options don't overwrite each other.
    queryKey: [KEY, 'hr-dashboard', range?.from ?? '', range?.to ?? ''],
    queryFn: () => getHrDashboard(range),
    enabled,
    staleTime: 60_000,
  });

const useApplicationsList = (query: ApplicationListQuery) =>
  useQuery({
    queryKey: [KEY, 'list', query],
    queryFn: () => listApplications(query),
    staleTime: 10_000,
  });

/** Adapts the `{ data, pagination }` envelope to what ListScreen expects. */
export const useAdaptedApplicationsList = (
  query: ApplicationListQuery,
): UseQueryResult<PaginatedResponse<CareerApplicationDetail>> => {
  const result = useApplicationsList(query);
  return useMemo(() => {
    if (!result.data) {
      return result as unknown as UseQueryResult<PaginatedResponse<CareerApplicationDetail>>;
    }
    const { page, pageSize, totalItems } = result.data.pagination;
    const totalPages = Math.ceil(totalItems / pageSize);
    const adapted: PaginatedResponse<CareerApplicationDetail> = {
      data: result.data.data,
      pagination: {
        page,
        pageSize,
        totalItems,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
    return {
      ...result,
      data: adapted,
    } as UseQueryResult<PaginatedResponse<CareerApplicationDetail>>;
  }, [result]);
};

export const useUpdateApplicationStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: CareerApplicationStatus }) =>
      updateApplicationStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Status updated');
    },
  });
};

export const useDeleteApplication = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteApplication,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Enquiry deleted');
    },
  });
};
